import { useState, type ReactNode } from 'react';
import type { Course, CourseHole } from '@/lib/db/types';
import { courseHandicap, scoreToParLabel, strokesPerHole } from '@/lib/courses/group';
import { estimateHoleYardage } from '@/lib/courses/yardage';
import { displayPlayerName } from '@/lib/display-name';
import { Avatar } from '@/components/shared/Avatar';
import { GpsCaddy } from '@/components/play/GpsCaddy';
import { HoleIllustration } from '@/components/play/HoleIllustration';

export interface GroupScorecardPlayer {
  id: string;
  name: string;
  handicap?: number | null;
  you?: boolean;
  guest?: boolean;
  avatar?: string | null;
  profile_photo_url?: string | null;
}

interface Props {
  holes: CourseHole[];
  teeSlope?: number | null;
  teeYardageTotal?: number | null;
  course?: Course | null;
  players: GroupScorecardPlayer[];
  scoresById: Record<string, (number | null)[]>;
  puttsById?: Record<string, (number | null)[]>;
  onScore: (playerId: string, holeIndex: number, value: number | null) => void;
  onPutt?: (playerId: string, holeIndex: number, value: number | null) => void;
  onHoleInOne?: (holeNum: number) => void;
  /** When true, scores are view-only (spectator / non-organizer). */
  readOnly?: boolean;
  /** When set, shown at top of scorecard instead of GPS Caddy (group round). */
  topBanner?: ReactNode;
  showGpsCaddy?: boolean;
}

function strokeName(score: number, par: number): string {
  const d = score - par;
  if (d <= -3) return 'Albatross';
  if (d === -2) return 'Eagle';
  if (d === -1) return 'Birdie';
  if (d === 0) return 'Par';
  if (d === 1) return 'Bogey';
  if (d === 2) return 'Double';
  if (d === 3) return 'Triple';
  return `+${d}`;
}

function accentColor(score: number | null, par: number): string {
  if (score == null) return 'var(--mute, #94a3b8)';
  const d = score - par;
  if (d <= -2) return '#8a6d3d';
  if (d === -1) return '#15803d';
  if (d === 0) return '#0a1e3f';
  return '#dc2626';
}

export function GroupScorecard({
  holes,
  teeSlope,
  teeYardageTotal,
  course,
  players,
  scoresById,
  puttsById = {},
  onScore,
  onPutt,
  onHoleInOne,
  topBanner,
  showGpsCaddy = true,
  readOnly = false,
}: Props) {
  const [activeHole, setActiveHole] = useState(0);
  const hole = holes[activeHole];
  if (!hole) {
    return (
      <div className="rounded-xl border border-line bg-paper p-4 text-center text-sm text-mute">
        Course holes not loaded yet.
      </div>
    );
  }

  const sortedHoles = [...holes].sort((a, b) => a.hole_num - b.hole_num);

  const scoreAt = (pid: string) => scoresById[pid]?.[activeHole] ?? null;
  const puttAt = (pid: string) => puttsById[pid]?.[activeHole] ?? null;

  const applyScore = (pid: string, val: number | null) => {
    const prev = scoreAt(pid);
    onScore(pid, activeHole, val);
    if (pid === players.find((p) => p.you)?.id && val === 1 && prev !== 1) {
      onHoleInOne?.(hole.hole_num);
    }
  };

  const strokesOnHole = (p: GroupScorecardPlayer) => {
    const ch = courseHandicap(p.handicap, teeSlope ?? 113);
    const dots = strokesPerHole(ch, sortedHoles);
    return dots[activeHole] ?? 0;
  };

  const chipScores = (() => {
    const out: number[] = [];
    for (let d = -2; d <= 3; d += 1) {
      const s = hole.par + d;
      if (s >= 1 && !out.includes(s)) out.push(s);
    }
    return out;
  })();

  const totals = players.map((p) => {
    const sa = scoresById[p.id] || [];
    const pa = puttsById[p.id] || [];
    let gross = 0;
    let thru = 0;
    let toPar = 0;
    let putts = 0;
    let hasPutts = false;
    holes.forEach((hh, i) => {
      const s = sa[i];
      if (typeof s === 'number' && Number.isFinite(s)) {
        gross += s;
        thru += 1;
        toPar += s - hh.par;
      }
      const pt = pa[i];
      if (typeof pt === 'number' && Number.isFinite(pt)) {
        putts += pt;
        hasPutts = true;
      }
    });
    return { id: p.id, gross, thru, toPar, putts, hasPutts };
  });

  const nextHoleNum = activeHole < holes.length - 1 ? holes[activeHole + 1].hole_num : null;
  const holeYards = estimateHoleYardage(hole.par, teeYardageTotal);
  const nameMaxChars = players.length >= 4 ? 10 : players.length >= 3 ? 12 : 16;
  const labelFor = (p: GroupScorecardPlayer) => displayPlayerName(p.name, { you: p.you, maxChars: nameMaxChars });
  const avatarFor = (p: GroupScorecardPlayer) => ({
    name: p.you ? 'You' : p.name,
    avatar: p.avatar ?? null,
    profile_photo_url: p.profile_photo_url ?? null,
  });

  return (
    <div>
      {topBanner}
      {showGpsCaddy && !topBanner && (
        <GpsCaddy course={course} holeNum={hole.hole_num} par={hole.par} teeYardageTotal={teeYardageTotal} />
      )}
      <div className="mb-3 overflow-hidden rounded-xl border border-line bg-paper p-2 shadow-bt">
        <HoleIllustration holeNum={hole.hole_num} par={hole.par} yards={holeYards} height={88} />
      </div>
      <div className="mb-3 flex items-center justify-between rounded-[18px] border border-gold-deep bg-gradient-to-br from-navy to-navy-mid p-3.5">
        <button
          type="button"
          disabled={activeHole === 0}
          onClick={() => setActiveHole((h) => Math.max(0, h - 1))}
          className="flex h-[54px] w-[54px] items-center justify-center rounded-[14px] border border-white/20 bg-white/10 text-[26px] font-bold text-white disabled:opacity-35"
        >
          ‹
        </button>
        <div className="text-center">
          <div className="text-[10px] font-bold tracking-[0.2em] text-gold-bright">HOLE</div>
          <div className="font-serif text-[44px] font-extrabold leading-none text-white">{hole.hole_num}</div>
          <div className="mt-1 text-xs font-semibold tracking-wide text-white/75">
            PAR {hole.par} · {holeYards} YDS · HCP {hole.stroke_index}
          </div>
          {players.some((p) => strokesOnHole(p) > 0) && (
            <div className="mt-1.5 flex justify-center gap-2">
              {players.filter((p) => strokesOnHole(p) > 0).slice(0, 2).map((p) => {
                const sh = strokesOnHole(p);
                return (
                  <span key={p.id} className="flex items-center gap-1 text-[9px] font-bold text-gold-bright">
                    {p.you ? 'You' : displayPlayerName(p.name, { maxChars: 8 })}
                    <span className="flex gap-0.5">
                      {Array.from({ length: sh }).map((_, j) => (
                        <span key={j} className="h-1 w-1 rounded-full bg-gold" />
                      ))}
                    </span>
                  </span>
                );
              })}
            </div>
          )}
        </div>
        <button
          type="button"
          disabled={activeHole >= holes.length - 1}
          onClick={() => setActiveHole((h) => Math.min(holes.length - 1, h + 1))}
          className="flex h-[54px] w-[54px] items-center justify-center rounded-[14px] border border-white/20 bg-white/10 text-[26px] font-bold text-white disabled:opacity-35"
        >
          ›
        </button>
      </div>

      {nextHoleNum != null ? (
        readOnly ? (
          <button
            type="button"
            onClick={() => setActiveHole((h) => Math.min(holes.length - 1, h + 1))}
            className="mb-3 flex w-full items-center justify-center gap-2 rounded-[14px] border border-line bg-paper py-3 text-sm font-bold text-navy"
          >
            Next hole ({nextHoleNum})
          </button>
        ) : (
        <button
          type="button"
          onClick={() => setActiveHole((h) => Math.min(holes.length - 1, h + 1))}
          className="mb-3 flex w-full items-center justify-center gap-2 rounded-[14px] bg-gold py-3.5 text-[15px] font-extrabold tracking-wide text-navy"
        >
          ✓ CONFIRM · NEXT HOLE ({nextHoleNum})
        </button>
        )
      ) : (
        <div className="mb-3 text-center text-[11px] font-bold tracking-wide text-gold-deep">
          FINAL HOLE · ALL {holes.length} IN VIEW
        </div>
      )}

      {players.map((p) => {
        const cur = scoreAt(p.id);
        const sh = strokesOnHole(p);
        return (
          <div key={p.id} className="mb-3 rounded-[14px] border border-line bg-paper p-3.5 shadow-bt">
            <div className="flex items-center gap-3">
              <div className={p.you ? 'rounded-full ring-2 ring-gold ring-offset-1' : undefined}>
                <Avatar member={avatarFor(p)} size={48} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-serif text-[17px] font-bold text-navy" title={p.you ? 'You' : p.name}>
                  {labelFor(p)}
                  {p.guest && <span className="ml-1 text-[10px] font-bold text-mute">GUEST</span>}
                </div>
                <div className={`flex items-center gap-1.5 text-[11px] font-semibold ${sh > 0 ? 'text-gold-deep' : 'text-mute'}`}>
                  {sh > 0 ? (
                    <>
                      <span className="flex gap-0.5" aria-hidden>
                        {Array.from({ length: sh }).map((_, j) => (
                          <span key={j} className="h-1.5 w-1.5 rounded-full bg-gold" />
                        ))}
                      </span>
                      <span>{sh} stroke{sh > 1 ? 's' : ''}</span>
                    </>
                  ) : (
                    'scratch hole'
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!readOnly && (
                <>
                <button
                  type="button"
                  onClick={() => applyScore(p.id, cur == null ? hole.par : Math.max(1, cur - 1))}
                  disabled={cur != null && cur <= 1}
                  className="flex h-[58px] w-[58px] items-center justify-center rounded-[14px] border border-line bg-cream-warm text-[28px] font-bold text-navy disabled:opacity-40"
                >
                  −
                </button>
                <div className="w-16 text-center">
                  <input
                    type="number"
                    inputMode="numeric"
                    value={cur ?? ''}
                    placeholder={String(hole.par)}
                    onChange={(e) => {
                      const t = e.target.value;
                      if (t === '') { applyScore(p.id, null); return; }
                      const n = parseInt(t, 10);
                      if (!Number.isNaN(n)) applyScore(p.id, Math.max(1, Math.min(20, n)));
                    }}
                    className="w-full border-none bg-transparent p-0 text-center font-serif text-[30px] font-extrabold outline-none"
                    style={{ color: cur == null ? '#e8e4d8' : accentColor(cur, hole.par) }}
                  />
                  <div className="text-[9px] font-extrabold tracking-wide text-mute">
                    {cur == null ? 'TAP A SCORE' : strokeName(cur, hole.par).toUpperCase()}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => applyScore(p.id, cur == null ? hole.par : cur + 1)}
                  className="flex h-[58px] w-[58px] items-center justify-center rounded-[14px] bg-gold text-[28px] font-bold text-navy"
                >
                  +
                </button>
                </>
                )}
                {readOnly && (
                <div className="ml-auto w-16 text-center">
                  <div
                    className="font-serif text-[30px] font-extrabold"
                    style={{ color: cur == null ? '#e8e4d8' : accentColor(cur, hole.par) }}
                  >
                    {cur ?? '—'}
                  </div>
                  <div className="text-[9px] font-extrabold tracking-wide text-mute">
                    {cur == null ? 'NO SCORE' : strokeName(cur, hole.par).toUpperCase()}
                  </div>
                </div>
                )}
              </div>
            </div>
            {!readOnly && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1 no-scrollbar">
              {chipScores.map((s) => {
                const sel = cur === s;
                const d = s - hole.par;
                const accent = accentColor(s, hole.par);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => applyScore(p.id, s)}
                    className="flex w-[60px] shrink-0 flex-col items-center gap-1 rounded-xl border py-2"
                    style={{
                      background: sel ? accent : '#ffffff',
                      borderColor: sel ? accent : '#e8e4d8',
                      color: sel ? '#fff' : accent,
                    }}
                  >
                    <span
                      className="flex h-7 w-7 items-center justify-center font-serif text-[17px] font-extrabold"
                      style={{
                        borderRadius: d < 0 ? '50%' : d > 0 ? 6 : 0,
                        border: d !== 0 ? `2px solid ${sel ? '#fff' : accent}` : 'none',
                      }}
                    >
                      {s}
                    </span>
                    <span className="text-[9px] font-bold" style={{ color: sel ? 'rgba(255,255,255,0.92)' : '#94a3b8' }}>
                      {strokeName(s, hole.par)}
                    </span>
                  </button>
                );
              })}
            </div>
            )}
            {!readOnly && onPutt && (
              <div className="mt-3 flex items-center gap-2.5 border-t border-line pt-3">
                <span className="text-[11px] font-extrabold tracking-wide text-mute">PUTTS</span>
                <button
                  type="button"
                  onClick={() => onPutt(p.id, activeHole, puttAt(p.id) == null ? 2 : Math.max(0, (puttAt(p.id) as number) - 1))}
                  disabled={puttAt(p.id) != null && (puttAt(p.id) as number) <= 0}
                  className="flex h-10 w-10 items-center justify-center rounded-[10px] border border-line bg-cream-warm text-lg font-bold disabled:opacity-40"
                >
                  −
                </button>
                <span className="min-w-[22px] text-center font-mono text-base font-bold text-navy">
                  {puttAt(p.id) == null ? '–' : puttAt(p.id)}
                </span>
                <button
                  type="button"
                  onClick={() => onPutt(p.id, activeHole, puttAt(p.id) == null ? 2 : (puttAt(p.id) as number) + 1)}
                  className="flex h-10 w-10 items-center justify-center rounded-[10px] border border-line bg-cream-warm text-lg font-bold"
                >
                  +
                </button>
              </div>
            )}
          </div>
        );
      })}

      <div className="overflow-hidden rounded-[14px] border border-line bg-paper shadow-bt">
        <div className="grid grid-cols-[1fr_44px_56px_52px_52px] bg-navy text-gold-bright">
          {['PLAYER', 'THRU', 'GROSS', '±', 'PUTTS'].map((t, i) => (
            <div key={t} className={`px-1.5 py-2 text-[9px] font-bold tracking-wide ${i === 0 ? 'text-left' : 'text-center'}`}>
              {t}
            </div>
          ))}
        </div>
        {players.map((p, ri) => {
          const t = totals[ri];
          return (
            <div key={p.id} className="grid grid-cols-[1fr_44px_56px_52px_52px] items-center border-t border-line">
              <div className="flex min-w-0 items-center gap-2 px-1.5 py-2">
                <Avatar member={avatarFor(p)} size={22} />
                <span className="truncate text-xs font-semibold text-ink" title={p.you ? 'You' : p.name}>
                  {labelFor(p)}
                </span>
              </div>
              <div className="text-center font-mono text-xs text-ink-soft">{t.thru}</div>
              <div className="text-center font-serif text-base font-bold text-navy">{t.thru ? t.gross : '–'}</div>
              <div className={`text-center font-mono text-xs font-bold ${t.toPar < 0 ? 'text-green-700' : t.toPar > 0 ? 'text-red-600' : 'text-navy'}`}>
                {t.thru ? scoreToParLabel(t.gross, t.gross - t.toPar) : '–'}
              </div>
              <div className="text-center font-mono text-xs text-ink-soft">{t.hasPutts ? t.putts : '–'}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
